import React from "react";
import { FileSignature, Clock, AlertTriangle, CheckCircle2, Send, Loader2, XCircle, Mail } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../lib/api";

/**
 * Compact stats widget for the admin "Ansøgninger" tab showing digital signing health.
 * - 4 KPI tiles (i dag / uge / måned / total signed)
 * - List of applications where link was sent >7 days ago but still not signed
 */
export default function ContractSigningStatsWidget({ onOpenApplication }) {
  const [stats, setStats] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [busyId, setBusyId] = React.useState(null);
  const [testing, setTesting] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const { data } = await adminApi().get("/admin/contract-signings/stats");
      setStats(data);
    } catch (e) {
      // silently ignore — widget is non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const sendTestEmail = async () => {
    if (!window.confirm("Send en TEST-mail med en dummy Burd-PDF vedhæftet til din admin-indbakke?\n\nDette verificerer at SMTP + PDF-vedhæftning virker end-to-end.")) return;
    setTesting(true);
    try {
      const { data } = await adminApi().post("/admin/contract-signings/send-test-email");
      if (data?.ok) {
        toast.success(`Test-mail sendt til ${data.sent_to} (${Math.round(data.size_bytes / 1024)} KB PDF)`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke sende test-mail");
    } finally {
      setTesting(false);
    }
  };

  const sendReminder = async (p, e) => {
    e.stopPropagation();
    if (!window.confirm(`Send påmindelse til ${p.full_name} (${p.email})?\n\nDe modtager en venlig mail med samme underskriv-link.`)) return;
    setBusyId(p.application_id);
    try {
      const { data } = await adminApi().post(`/admin/driver-applications/${p.application_id}/contract/send-reminder`);
      if (data?.ok) {
        toast.success(`Påmindelse sendt til ${data.sent_to}`);
        await load();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke sende påmindelse");
    } finally {
      setBusyId(null);
    }
  };

  const abandon = async (p, e) => {
    e.stopPropagation();
    if (!window.confirm(`Marker ${p.full_name} som "opgivet"?\n\nAuto-rykkere stopper. Ansøgningen forbliver i listen, men du kan slette den manuelt hvis du vil.`)) return;
    setBusyId(p.application_id);
    try {
      const { data } = await adminApi().post(`/admin/driver-applications/${p.application_id}/contract/abandon`);
      if (data?.ok) {
        toast.success("Markeret som opgivet — ingen flere auto-rykkere");
        await load();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke opgive");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-brand p-5 text-sm text-brand-text2" data-testid="signing-stats-loading">
        Indlæser underskrifts-statistik…
      </div>
    );
  }
  if (!stats) return null;

  const tiles = [
    { label: "I dag", value: stats.signed_today, icon: CheckCircle2, accent: "text-emerald-700 border-emerald-300" },
    { label: "Sidste 7 dage", value: stats.signed_week, icon: FileSignature, accent: "text-brand-dark border-brand" },
    { label: "Sidste 30 dage", value: stats.signed_month, icon: FileSignature, accent: "text-brand-dark border-brand" },
    { label: "Total underskrevet", value: stats.signed_total, icon: FileSignature, accent: "text-brand-dark border-brand" },
  ];

  return (
    <div className="space-y-3" data-testid="signing-stats-widget">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <div key={t.label} className={`bg-white border ${t.accent} p-4`} data-testid={`signing-tile-${t.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="kicker">{t.label}</div>
                  <div className={`mt-1 text-3xl font-display font-bold ${t.accent.split(" ")[0]}`}>{t.value}</div>
                </div>
                <Icon size={18} className={t.accent.split(" ")[0]} />
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-end">
        <button
          onClick={sendTestEmail}
          disabled={testing}
          className="text-xs border border-brand-dark text-brand-dark hover:bg-brand-dark hover:text-white px-3 py-1.5 flex items-center gap-1.5 transition-colors disabled:opacity-60"
          data-testid="send-test-burd-email-btn"
          title="Send en test-mail med dummy Burd-PDF for at verificere SMTP + vedhæftning"
        >
          {testing ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
          {testing ? "Sender test-mail…" : "Test admin-mail (dummy PDF)"}
        </button>
      </div>

      {stats.pending_old && stats.pending_old.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 p-4" data-testid="signing-pending-old">
          <div className="flex items-center gap-2 text-amber-900 font-semibold">
            <AlertTriangle size={16} />
            <span>Afventer underskrift &gt; 7 dage ({stats.pending_old.length})</span>
          </div>
          <ul className="mt-2 space-y-1 text-sm">
            {stats.pending_old.map((p) => (
              <li
                key={p.application_id}
                className="flex items-center justify-between gap-2 hover:bg-amber-100 px-2 py-1 -mx-2"
                data-testid={`pending-old-${p.application_id}`}
              >
                <div
                  className="flex items-center gap-2 min-w-0 cursor-pointer flex-1"
                  onClick={() => onOpenApplication?.(p.application_id)}
                >
                  <Clock size={14} className="text-amber-700 flex-shrink-0" />
                  <span className="font-medium truncate">{p.full_name}</span>
                  <span className="text-amber-800 text-xs truncate">· {p.email}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-amber-800 font-semibold">{p.days_waiting} dage</span>
                  <button
                    onClick={(e) => sendReminder(p, e)}
                    disabled={busyId === p.application_id}
                    className="text-xs bg-brand-dark text-white hover:bg-brand-red px-2 py-1 flex items-center gap-1"
                    data-testid={`send-reminder-${p.application_id}`}
                    title="Send venlig påmindelse via email"
                  >
                    {busyId === p.application_id ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
                    Send rykker
                  </button>
                  <button
                    onClick={(e) => abandon(p, e)}
                    disabled={busyId === p.application_id}
                    className="text-xs border border-amber-400 text-amber-900 hover:bg-amber-200 px-2 py-1 flex items-center gap-1"
                    data-testid={`abandon-${p.application_id}`}
                    title="Marker som opgivet — stopper auto-rykkere"
                  >
                    <XCircle size={11} />
                    Opgiv
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-3 text-xs text-amber-700 italic">
            ℹ Systemet sender automatisk rykker hver 5. dag (max 3 gange). Klik "Opgiv" for at stoppe rykkerne for en ansøger.
          </div>
        </div>
      )}
    </div>
  );
}
